import { Fragment } from 'react';
import { getLeadStatuses, LEAD_STATUS_COLORS } from '../utils/leadStatus';

/**
 * Shared Component/Goal → Lead-status breakdown table. Used by Lead QA
 * Review's Component/Goal Rollup, Supplier Portal's Current Status, and My
 * Home's "Your Programs" panel — one implementation so the three can't
 * drift the way this codebase's Rollup-vs-tabs counts and dual DeliveryCard
 * history already have (see CLAUDE.md). Purely presentational — the page
 * fetches its own data and maps it into `rows`.
 *
 * Props:
 *   rows: [{ key, label, groupLabel?, counts: {status: count}, ...extra }]
 *     - groupLabel, when present and changing from the previous row, renders
 *       a section header above that row (e.g. component name). Omit on
 *       every row for a flat list (Supplier Portal's case).
 *     - any extra fields a caller's extraColumns/renderStatusCell reads
 *       (e.g. qty, rejectedClient) live alongside counts on the row object.
 *   includesCallReview: bool — whether to include the 6th status column
 *   title?: string — header bar text (omit for no header bar)
 *   onRefresh?: () => void — header bar refresh button, only shown if passed
 *   extraColumns?: [{ key, header, align?, insertAfter?, render(row),
 *     renderTotal?(rows, statusTotals) }]
 *     - insertAfter: 'label' | <status name> | undefined (undefined/'end' =
 *       appended after every status column)
 *   renderStatusHeader?: (status) => full <th>...</th> | undefined
 *     — same full-cell-override contract as renderStatusCell, for the
 *       header row (e.g. Supplier Portal's per-status download icon).
 *   renderStatusCell?: (status, count, row) => full <td>...</td> | undefined
 *     — return undefined to fall through to the default cell; return a
 *       whole <td> (not just its content) to customize one status's cell
 *       entirely — style/title/cursor included (e.g. LeadQA's Rejected
 *       hover tooltip) — without losing it as a real status column. Set
 *       key={status} on the returned <td> yourself (it's inserted directly
 *       into a flatMap array).
 *   renderStatusTotalCell?: (status, total, rows) => full <td>...</td> | undefined
 *   compact?: bool — condensed sizing for a narrow context (My Home/
 *     Supplier Portal's `ProgramCarousel`): smaller header/cell font and
 *     padding, header text wraps onto 2-3 lines so a multi-word status name
 *     doesn't force its column wide, but each row's own cells stay one line
 *     (`whiteSpace: nowrap`) so counts never look squeezed or stretch a row
 *     tall — only the header row grows to fit its wrapped text. Column
 *     widths are left to the browser's normal table layout (content-based,
 *     not an equal 1/N split) so a handful of extraColumns (Goal/Target/
 *     Total) sit comfortably alongside the status columns instead of every
 *     column being forced to the same width regardless of what it holds —
 *     that equal-split (`table-layout: fixed`) is what caused the "squished
 *     and stretched" look before this. LeadQA's rollup keeps the default
 *     (wider container, no need to condense).
 *   onRowClick?: (row) => void — makes a top-level row's `<tr>` clickable
 *     (cursor + hover). This component holds no expand/collapse state of its
 *     own — the caller decides per-row whether it's expanded (see `subRows`).
 *   subRows?: (row) => rows[] | null | undefined — when it returns a
 *     non-empty array for a row, those rows render as additional, indented
 *     `<tr>`s directly beneath it, through the exact same column set
 *     (extraColumns/renderStatusCell/etc. all apply identically) — **not** a
 *     nested table with its own header. Matches `Programs.js`'s Drilldown
 *     expand-a-component-to-show-its-goals precedent: one header, one Total
 *     row (still computed from the top-level `rows` only, so a sub-row's
 *     counts are never double-counted into it), sub-rows just indented and
 *     visually subordinate. Added for Lead QA Review's Component Rollup —
 *     an earlier version rendered a second full `StatusBreakdownTable`
 *     per expanded component, which duplicated the header/Total row and
 *     looked like two stacked tables; this renders genuinely nested rows
 *     instead. A sub-row is never itself clickable — only top-level rows
 *     receive `onRowClick`.
 */
export default function StatusBreakdownTable({
  rows,
  includesCallReview,
  title,
  onRefresh,
  extraColumns = [],
  renderStatusHeader,
  renderStatusCell,
  renderStatusTotalCell,
  compact = false,
  onRowClick,
  subRows,
}) {
  const statuses = getLeadStatuses(includesCallReview);

  const extrasAfter = (anchor) =>
    extraColumns.filter((c) => (c.insertAfter || 'end') === anchor);
  const extrasAtEnd = extrasAfter('end');

  const statusTotals = Object.fromEntries(
    statuses.map((s) => [s, rows.reduce((sum, r) => sum + (r.counts?.[s] || 0), 0)])
  );

  const thBase = compact
    ? { padding: '3px 4px', background: '#2c3e50', color: 'white', fontSize: '9px', fontWeight: '600', textAlign: 'right', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.15' }
    : { padding: '6px 10px', background: '#2c3e50', color: 'white', fontSize: '11px', fontWeight: '600', textAlign: 'right', whiteSpace: 'nowrap' };
  const thLeft = { ...thBase, textAlign: 'left' };
  const tdBase = compact
    ? { padding: '3px 4px', fontSize: '10.5px', textAlign: 'right', borderBottom: '1px solid #eee', whiteSpace: 'nowrap' }
    : { padding: '5px 10px', fontSize: '12px', textAlign: 'right', borderBottom: '1px solid #eee', whiteSpace: 'nowrap' };
  const tdLeft = { ...tdBase, textAlign: 'left', fontWeight: '600', color: '#2c3e50' };

  const table = (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead>
          <tr>
            <th style={thLeft}>Label</th>
            {extrasAfter('label').map((c) => (
              <th key={c.key} style={thBase}>{c.header}</th>
            ))}
            {statuses.flatMap((s) => {
              const customTh = renderStatusHeader?.(s);
              return [
                customTh !== undefined ? customTh : <th key={s} style={thBase}>{s}</th>,
                ...extrasAfter(s).map((c) => <th key={c.key} style={thBase}>{c.header}</th>),
              ];
            })}
            {extrasAtEnd.map((c) => (
              <th key={c.key} style={thBase}>{c.header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => {
            const prevGroup = idx > 0 ? rows[idx - 1].groupLabel : undefined;
            const showGroupHeader = r.groupLabel !== undefined && r.groupLabel !== prevGroup;
            const totalCols = 1 + extraColumns.length + statuses.length;
            const childRows = subRows ? (subRows(r) || []) : [];
            const renderDataRow = (row, { sub = false } = {}) => (
              <tr
                key={row.key}
                onClick={!sub && onRowClick ? () => onRowClick(row) : undefined}
                style={{ background: sub ? '#fff' : '#fafafa', cursor: !sub && onRowClick ? 'pointer' : undefined }}>
                <td style={sub ? { ...tdLeft, fontWeight: '500', color: '#555', paddingLeft: '28px' } : tdLeft}>{row.label}</td>
                {extrasAfter('label').map((c) => (
                  <td key={c.key} style={tdBase}>{c.render(row)}</td>
                ))}
                {statuses.flatMap((s) => {
                  const count = row.counts?.[s] || 0;
                  const custom = renderStatusCell?.(s, count, row);
                  const cell = custom !== undefined ? custom : (
                    <td key={s} style={{ ...tdBase, color: LEAD_STATUS_COLORS[s]?.color || '#555' }}>{count}</td>
                  );
                  return [
                    cell,
                    ...extrasAfter(s).map((c) => <td key={c.key} style={tdBase}>{c.render(row)}</td>),
                  ];
                })}
                {extrasAtEnd.map((c) => (
                  <td key={c.key} style={tdBase}>{c.render(row)}</td>
                ))}
              </tr>
            );
            return (
              <Fragment key={r.key}>
                {showGroupHeader && (
                  <tr key={`${r.key}-group`}>
                    <td colSpan={totalCols} style={{ padding: '7px 10px 3px', fontSize: '10.5px', fontWeight: '700', letterSpacing: '0.03em', textTransform: 'uppercase', color: '#8a93a1', borderBottom: 'none' }}>
                      {r.groupLabel}
                    </td>
                  </tr>
                )}
                {renderDataRow(r)}
                {childRows.map((sr) => renderDataRow(sr, { sub: true }))}
              </Fragment>
            );
          })}
          <tr style={{ background: '#eaf0fb', fontWeight: '700' }}>
            <td style={{ ...tdBase, textAlign: 'left', color: '#2c3e50' }}>Total</td>
            {extrasAfter('label').map((c) => (
              <td key={c.key} style={tdBase}>{c.renderTotal ? c.renderTotal(rows, statusTotals) : ''}</td>
            ))}
            {statuses.flatMap((s) => {
              const custom = renderStatusTotalCell?.(s, statusTotals[s], rows);
              const cell = custom !== undefined ? custom : (
                <td key={s} style={{ ...tdBase, color: LEAD_STATUS_COLORS[s]?.color || '#555' }}>{statusTotals[s]}</td>
              );
              return [
                cell,
                ...extrasAfter(s).map((c) => (
                  <td key={c.key} style={tdBase}>{c.renderTotal ? c.renderTotal(rows, statusTotals) : ''}</td>
                )),
              ];
            })}
            {extrasAtEnd.map((c) => (
              <td key={c.key} style={tdBase}>{c.renderTotal ? c.renderTotal(rows, statusTotals) : ''}</td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );

  if (!title && !onRefresh) return table;

  return (
    <div style={{ marginBottom: '16px', border: '1px solid #dde', borderRadius: '6px', overflow: 'hidden' }}>
      {(title || onRefresh) && (
        <div style={{ padding: '8px 12px', background: '#2c3e50', color: 'white', fontSize: '12px', fontWeight: '700', letterSpacing: '0.5px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{title}</span>
          {onRefresh && (
            <button onClick={onRefresh}
              title="Reload — these counts don't auto-update after an accept/QA action taken elsewhere"
              style={{ padding: '2px 9px', background: 'transparent', border: '1px solid #7f95ab', color: 'white', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: '400', letterSpacing: 'normal' }}>
              🔄 Refresh
            </button>
          )}
        </div>
      )}
      {table}
    </div>
  );
}
